import React from "react";
import { Button, Pagination, PlusIcon } from "@pd/pd-ui-kit";
import { format } from "date-fns";
import { get } from "lodash";
import { useTranslation } from "react-i18next";
import { useHistory, useParams } from "react-router-dom";

import Wrapper from "~/shared/ui/Wrapper";
import { DocumentStatus } from "~/components/Forms/CreateDocument/utils/utils";
import { useGetLocationData, useSetLocationData } from "~/hooks/locationData";
import {
  getEndOfDayWithTimestamp,
  getFullTimeWithTimestamp,
} from "~/libs/getDateOptions";
import { TDocument_identifier } from "~/model/Instances/TDocument";
import {
  useGetCatalogQuery,
  useGetDocumentListQuery,
} from "~/schemas/generated/graphql";
import { usePermissions } from "~/shared/context/PermissionContext";
import { usePaginationParams } from "~/shared/hooks/usePaginationParams";
import { TableV2, useTableSort } from "~/shared/ui/TableV2";
import { DefaultRowControl } from "~/shared/ui/TableV2/ui/DefaultRowControl";

import DocumentsFilter from "./Filter";
import DocumentModal from "./modals/DocumentModal";
import DocumentStatusModal from "./modals/StatusModal";
import {
  DOCUMENT_SORTABLE_COLUMNS,
  getDocumentsColumns,
} from "./model/tableAdapter";
import { DOCUMENT_OPTIONS } from "./utils/options";

import s from "./s.less";

type Params = {
  id: string;
};

const Documents = () => {
  const history = useHistory();
  const { id } = useParams<Params>();
  const { t } = useTranslation();
  const setData = useSetLocationData();
  // Открытие/закрытие модалок — через replace, чтобы не плодить историю браузера
  const setDataReplace = useSetLocationData(true);
  const { hasPermission } = usePermissions();
  const {
    page,
    count,
    sortField,
    sortType: sortTypeRaw,
  } = useGetLocationData();
  // Ключи фильтров приходят из URL и не описаны в типе useGetLocationData
  const {
    name,
    description,
    signedBy,
    importance,
    status,
    createdAtFrom,
    createdAtTo,
  } = useGetLocationData() as Record<string, unknown>;

  // Бэкенд DocumentGetter.getList не принимает order/orderBy,
  // поэтому сортировка клиентская, а в URL хранится только её состояние
  const { sortType, onSortChange } = useTableSort({
    sortField,
    sortType: sortTypeRaw,
    sortableColumns: DOCUMENT_SORTABLE_COLUMNS,
    setData,
  });

  const tableSortField =
    sortField && DOCUMENT_SORTABLE_COLUMNS.includes(sortField)
      ? sortField
      : undefined;

  const { data: catalogData, loading: catalogLoading } = useGetCatalogQuery({
    variables: { id },
    fetchPolicy: "no-cache",
  });

  const selectedCatalog = get(catalogData, "DocumentGetter.getOneSection");

  const { data, loading, refetch } = useGetDocumentListQuery({
    variables: {
      sectionId: id,
      page,
      count,
      name: name ? String(name) : undefined,
      description: description ? String(description) : undefined,
      signedBy: signedBy ? String(signedBy) : undefined,
      importance: importance ? String(importance) : undefined,
      status: status ? String(status) : undefined,
      createdAtFrom: createdAtFrom
        ? new Date(getFullTimeWithTimestamp(String(createdAtFrom)))
        : undefined,
      createdAtTo: createdAtTo
        ? new Date(getEndOfDayWithTimestamp(String(createdAtTo)))
        : undefined,
    },
    fetchPolicy: "no-cache",
  });

  const documentList = get(data, "DocumentGetter.getList.items", []).map(
    (el) => ({
      ...el,
      createdAtRaw: el.createdAt ? new Date(el.createdAt).getTime() : 0,
      createdAt: format(new Date(el.createdAt), "dd.MM.yyyy"),
    }),
  );
  const documentListTotal = get(data, "DocumentGetter.getList.total", 0);

  const { handleCount, handlePage } = usePaginationParams();

  const openCreateModal = () => setDataReplace("openModal", "Create");

  const handleRowEdit = (record: { id: string | number }) => {
    setDataReplace("openModal", "Edit", "currentId", record.id);
  };

  const handleRowEditStatus = (record: { id: string | number }) => {
    setDataReplace("openModal", "EditStatus", "currentId", record.id);
  };

  const resolveRowEdit = (record: { status?: DocumentStatus }) => {
    const statusToEdit = [DocumentStatus.Draft, DocumentStatus.Revoked];
    if (!record.status || !statusToEdit.includes(record.status))
      return undefined;
    return handleRowEdit;
  };

  const goToDocumentView = ({ id }: TDocument_identifier) =>
    history.push(`/documents/viewing/${id}`);

  const columns = getDocumentsColumns(
    t,
    DOCUMENT_OPTIONS,
    tableSortField,
    sortType,
  );

  const title = `${t("documents.subcatalog")} ${
    catalogLoading ? "..." : (selectedCatalog?.name ?? "")
  }`;

  return (
    <Wrapper.Content
      title={title}
      txtBtn={t("ui.back")}
      goBackHandler={() =>
        history.push(
          selectedCatalog?.parentId
            ? `/documents/sub_catalogs/${selectedCatalog.parentId}`
            : "/documents/",
        )
      }
      filterComponent={<DocumentsFilter />}
      controlComponent={
        hasPermission("documents.create") ? (
          <Button
            onClick={openCreateModal}
            size={"middle"}
            icon={<PlusIcon size={16} />}
          >
            {t("documentLib.documentCreate")}
          </Button>
        ) : undefined
      }
    >
      <TableV2
        wrapperClassName={s.tableWrapper}
        columns={columns}
        dataSource={documentList}
        rowKey="id"
        loading={loading}
        rowActions={(record) => (
          <DefaultRowControl
            record={record}
            onRowEdit={
              hasPermission("documents.edit")
                ? resolveRowEdit(record)
                : undefined
            }
            onRowSettings={
              hasPermission("documents.setStatus")
                ? handleRowEditStatus
                : undefined
            }
          />
        )}
        actionsFixed
        actionsPlacement="left"
        sortableColumns={DOCUMENT_SORTABLE_COLUMNS}
        sortField={tableSortField}
        sortType={tableSortField ? sortType : undefined}
        onSortChange={onSortChange}
        onRow={(record) => ({
          onClick: () => goToDocumentView(record),
          style: { cursor: "pointer" },
        })}
      />
      <Pagination
        count={count}
        page={page + 1}
        totalCount={documentListTotal}
        onChangeCount={handleCount}
        onChangePage={handlePage}
      />
      <DocumentModal catalog={selectedCatalog} reFetchTable={refetch} />
      <DocumentStatusModal reFetchTable={refetch} />
    </Wrapper.Content>
  );
};

export default Documents;
